/**
 * Traduction et formatage localisé.
 *
 * L'application Android s'appuie sur la sélection de langue par application
 * d'Android ; côté web, la préférence est stockée et appliquée à `<html lang>`.
 */

import type { LanguagePreference } from '../core/preferences'
import { STRINGS, type Locale, type StringKey } from './strings'

export type { Locale, StringKey }

/** Résout la préférence de langue en langue effective. */
export function resolveLocale(preference: LanguagePreference): Locale {
  if (preference === 'fr' || preference === 'en') {
    return preference
  }

  const languages =
    typeof navigator !== 'undefined' ? (navigator.languages ?? [navigator.language]) : []

  for (const language of languages) {
    if (typeof language !== 'string') continue
    if (language.toLowerCase().startsWith('fr')) return 'fr'
    if (language.toLowerCase().startsWith('en')) return 'en'
  }

  return 'en'
}

export class Translator {
  #locale: Locale

  constructor(locale: Locale = 'en') {
    this.#locale = locale
  }

  get locale(): Locale {
    return this.#locale
  }

  setLocale(locale: Locale): void {
    this.#locale = locale
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale
    }
  }

  /**
   * Traduit une clé, en substituant les paramètres nommés `{nom}`.
   *
   * Une clé inconnue est renvoyée telle quelle : en développement, cela rend
   * l'oubli visible à l'écran plutôt que de le masquer par une chaîne vide.
   */
  t(key: StringKey, params?: Record<string, string | number>): string {
    const table = STRINGS[this.#locale] as Record<string, string>
    const template = table[key] ?? (STRINGS.en as Record<string, string>)[key] ?? key

    if (!params) {
      return template
    }

    return template.replace(/\{(\w+)\}/g, (match, name: string) => {
      const value = params[name]
      return value === undefined ? match : String(value)
    })
  }

  /** Nombre formaté selon la langue active (virgule décimale en français). */
  number(value: number, fractionDigits = 1): string {
    return new Intl.NumberFormat(this.#locale, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value)
  }

  /** Date courte et heure, pour l'horodatage du cache des radars. */
  dateTime(timestamp: number): string {
    return new Intl.DateTimeFormat(this.#locale, {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(timestamp))
  }

  /** Taille de fichier lisible, avec les unités de la langue active. */
  fileSize(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} ${this.#locale === 'fr' ? 'o' : 'B'}`
    }
    if (bytes < 1024 * 1024) {
      const unit = this.#locale === 'fr' ? 'ko' : 'KB'
      return `${Math.floor(bytes / 1024)} ${unit}`
    }
    const unit = this.#locale === 'fr' ? 'Mo' : 'MB'
    return `${this.number(bytes / (1024 * 1024), 1)} ${unit}`
  }
}
